import * as XLSX from 'xlsx';
import { normalizeOrderDate, resolveOrderCode, orderCode } from './orders';
import { bindingPlan } from './bindings';
import { isCancelled } from './cancellation';

export const text = value => String(value ?? '').trim();
export const normalized = value => text(value).toLocaleUpperCase('tr-TR').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/İ/g, 'I').replace(/[^A-Z0-9]/g, '');
export const IMPORT_FIELDS = [
  ['date', 'Teslimat tarihi', ['Teslimat Tarihi', 'Dolum Tarihi', 'Tarih']],
  ['waybill', 'Fabrika irsaliye no', ['E-İrsaliye No', 'İrsaliye No', 'Fabrika İrsaliye No']],
  ['factory', 'Fabrika', ['Kredi Kontrol', 'Fabrika', 'Fabrika Adı']],
  ['mta', 'MTA / Şantiye', ['Şantiye Adı', 'MTA', 'Teslim Yeri']],
  ['product', 'Ürün', ['Ürün Adı', 'Çimento Cinsi', 'Malzeme']],
  ['ton', 'Tonaj', ['Miktar', 'Tonaj', 'Ton']],
  ['plate', 'Plaka', ['Plaka', 'Araç Plakası']],
  ['order', 'Sipariş kodu', ['Sipariş No', 'Sipariş Kodu', 'Sipariş Numarası']],
  ['sap', 'SAP teslimat no', ['Sap Teslimat No']],
  ['mtaCode', 'Şantiye kodu', ['Şantiye No', 'MTA Kodu']],
  ['productCode', 'Ürün kodu', ['Ürün Kodu', 'Malzeme Kodu']],
  ['unit', 'Miktar birimi', ['Ö.B', 'Birim']],
];
export const REQUIRED_COLUMNS = ['date', 'waybill', 'factory', 'mta', 'product', 'ton'];
export const aliasKey = (type, scope, value) => encodeURIComponent(JSON.stringify([type, scope, normalized(value)])).replace(/\./g, '%2E');
export const formatKey = headers => encodeURIComponent(JSON.stringify(headers.map(normalized))).replace(/\./g, '%2E');
export function suggestColumns(headers) {
  return Object.fromEntries(IMPORT_FIELDS.map(([key, , aliases]) => [key, String(headers.findIndex(header => aliases.some(alias => normalized(alias) === normalized(header))))]));
}
export function importDate(value) {
  if (value instanceof Date && Number.isFinite(value.getTime())) return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    return parsed ? normalizeOrderDate(`${parsed.y}-${String(parsed.m).padStart(2, '0')}-${String(parsed.d).padStart(2, '0')}`) : '';
  }
  const raw = text(value);
  const local = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  return normalizeOrderDate(local ? `${local[3]}-${local[2].padStart(2, '0')}-${local[1].padStart(2, '0')}` : raw);
}
export function importTon(value) {
  if (typeof value === 'number') return value;
  let raw = text(value).replace(/\s/g, '');
  if (raw.includes(',')) raw = raw.replace(/\./g, '').replace(',', '.');
  return raw ? Number(raw) : NaN;
}
export function parseImportRows(rows, columns, headerIndex = 0) {
  return rows.slice(headerIndex + 1).map((cells, index) => ({
    rowId: `excel-${headerIndex + index + 2}`, rowNumber: headerIndex + index + 2,
    ...Object.fromEntries(IMPORT_FIELDS.map(([key]) => [key, Number(columns[key]) >= 0 && columns[key] !== '' ? cells[Number(columns[key])] ?? '' : ''])),
  })).filter(row => REQUIRED_COLUMNS.some(key => text(row[key]))).map(row => ({ ...row, date: importDate(row.date), ton: importTon(row.ton), rawDate: text(row.date) }));
}
const uniqueNamed = (items, value) => {
  const found = items.filter(item => normalized(item.ad) === normalized(value));
  return found.length === 1 ? found[0]?.id : '';
};
export function resolveImportRow(raw, { factories, mtas, aliases = {}, selections = {} }) {
  const factoryId = selections[aliasKey('factory', '', raw.factory)] ?? aliases[aliasKey('factory', '', raw.factory)] ?? uniqueNamed(factories, raw.factory);
  const factory = factories.find(item => item.id === factoryId && item.aktif !== false);
  const mtaKey = aliasKey('mta', factoryId || text(raw.factory), raw.mtaCode || raw.mta);
  const productKey = aliasKey('product', factoryId || text(raw.factory), raw.productCode || raw.product);
  const regional = mtas.filter(item => item.aktif !== false && item.bolgeId === factory?.bolgeId);
  const mtaId = selections[mtaKey] ?? aliases[mtaKey] ?? uniqueNamed(regional, raw.mta);
  const products = factory?.cimentoCinsleri || [];
  const matches = products.filter(item => normalized(item) === normalized(raw.product));
  const product = selections[productKey] ?? aliases[productKey] ?? (matches.length === 1 ? matches[0] : '');
  return { ...raw, factoryId: factory?.id || '', mtaId: regional.some(item => item.id === mtaId) ? mtaId : '', product: products.includes(product) ? product : '', sourceProduct: raw.product, factoryKey: aliasKey('factory', '', raw.factory), mtaKey, productKey };
}
export const duplicateKey = row => `${row.fabrikaId}|${orderCode(row.fabrikaIrsaliyeNo)}`;
export function previewStatus(row, records, seen = new Set()) {
  const errors = [];
  if (!row.date) errors.push('Geçersiz / eksik tarih');
  if (!text(row.waybill)) errors.push('İrsaliye eksik');
  if (!row.factoryId) errors.push('Fabrika seçin');
  if (!row.mtaId) errors.push('MTA seçin');
  if (!row.product) errors.push('Çimento cinsi seçin');
  if (!Number.isFinite(row.ton) || row.ton <= 0) errors.push('Geçerli tonaj girin');
  if (text(row.unit) && !['TO', 'TON', 'T', 'TONNE'].includes(normalized(row.unit))) errors.push('Miktar birimi ton olmalı');
  const key = duplicateKey({ fabrikaId: row.factoryId, fabrikaIrsaliyeNo: row.waybill });
  const existing = row.factoryId && records.find(record => !isCancelled(record) && duplicateKey(record) === key);
  if (existing) {
    const same = existing.dolumTarihi === row.date && existing.mtaId === row.mtaId && normalized(existing.cimentoCinsi) === normalized(row.product) && Math.abs(Number(existing.miktarTon) - row.ton) < 0.000001 && (!text(row.plate) || normalized(existing.plaka) === normalized(row.plate));
    return { state: same ? 'EXISTS' : 'CONFLICT', message: same ? 'Zaten kayıtlı · atlanacak' : 'Mevcut irsaliye ile bilgiler farklı · kaydedilmez', existing };
  }
  if (row.factoryId && text(row.waybill)) {
    if (seen.has(key)) errors.push('Dosyada aynı irsaliye tekrar ediyor');
    seen.add(key);
  }
  return { state: errors.length ? 'ERROR' : 'READY', message: errors.join(' · ') || 'Kayda hazır' };
}
export function externalPayload(row, { factories, mtas, regions, orders, factoryBindings, mtaBindings }) {
  const factory = factories.find(item => item.id === row.factoryId);
  const mta = mtas.find(item => item.id === row.mtaId);
  if (!factory || !mta) throw new Error('Fabrika ve MTA seçimini kontrol edin.');
  const base = {
    kaynakTipi: 'HARICI', teslimTipi: 'FABRIKA_TESLIM', durum: 'GONDERILDI',
    dolumTarihi: row.date, dokumTarihi: row.date, sevkTarihi: row.date,
    fabrikaId: factory.id, fabrikaAdi: factory.ad, bolgeId: factory.bolgeId,
    operasyonBolgesi: regions.find(item => item.id === factory.bolgeId)?.ad || '',
    tedarikciId: factory.tedarikciId || '', tedarikciAdi: factory.tedarikciAdi || '',
    mtaId: mta.id, mtaAdi: mta.ad, aliciAdi: mta.ad, musteriId: mta.musteriId || '', musteriAdi: mta.musteriAdi || '',
    cimentoCinsi: row.product, miktarTon: row.ton, plaka: orderCode(row.plate).replace(/\s/g, ''),
    seriNo: '', soforId: '', soforAdi: '', dolumKm: null, dokumKm: null,
    fabrikaIrsaliyeNo: orderCode(row.waybill), sapTeslimatNo: text(row.sap),
    siparisKodu: orderCode(row.order), siparisId: '', siparisTakibi: !!text(row.order), siparisOnayDurumu: 'BEKLIYOR',
  };
  const order = resolveOrderCode(orders, base);
  if (order) Object.assign(base, { siparisId: order.id, siparisKodu: order.siparisKodu });
  return { ...base, ...bindingPlan(base, factoryBindings, mtaBindings, orders) };
}
